import { useState, useMemo } from "react";
import DescriptionBanner from "./DescriptionBanner";
import SpiderChart from "./SpiderChart";
import { Icon } from "./UI/Icon";
import CriticalityBadge from "./CriticalityBadge";
import CountUpAnimation from "./UI/CountUpAnimation";
import ClipLoader from "react-spinners/ClipLoader";
import { toast } from "react-toastify";

import API_ENDPOINTS from "../Config/api";
import { fetchWithAuth } from "../Utils/fetchWithAuth";
import useGet from "../Hooks/useGet";

import { useRecoilValue } from 'recoil';
import { disableForEnterpriseAtom } from '../Atom/disableForEnterpriseAtom';
import { useTranslation } from 'react-i18next';

export default function Nis2Risk({
    nis2ReportData,
    isVisibleDescriptionBanner = true,
    isVisibleDownloadReport = true,
}) {

    const [loading, setLoading] = useState(false);
    const { fetchedData: aziendaDettagli } = useGet(API_ENDPOINTS.aziendaDettagli);

    const disableForEnterprise = useRecoilValue(disableForEnterpriseAtom)
    const { t } = useTranslation();

    // Safeguard
    const percentuale = nis2ReportData?.percentuale_conformita ?? 0;
    const categorieEntries = useMemo(
        () => Object.entries(nis2ReportData?.categorie ?? {}),
        [nis2ReportData?.categorie]
    );

    const data = useMemo(
        () => ({
            labels: categorieEntries.map(([key]) => (t(`nis2Categories.${key}`, '') || key).split(/ (?=\S+$)/)),
            datasets: [
                {
                    label: t('nis2.your_perimeter'),
                    data: categorieEntries.map(([, value]) => value?.percentuale ?? 0),
                    borderColor: "#FACC15",
                    backgroundColor: "rgba(250, 204, 21, 0.2)",
                    borderWidth: 2,
                    pointRadius: 3,
                },
            ],
        }),
        [categorieEntries, t]
    );

    const onExportPdf = async () => {

        setLoading(true)
        const payload = {
            flags: {
                dati_azienda_asset: true,
                compliance_nis2: true,
                ambiti_nis2: true,
                nis2_criticita: true,
                file_angel: true,
                file_angel_time_stats: true,
                cyber_angel: true,
                browsing_angel: true,
                browsing_angel_time_stats: true,
                cyber_vulnerabilita: true
            }
        };


        try {
            const res = await fetchWithAuth(API_ENDPOINTS.exportPDF, {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error('Errore durante la generazione del PDF');

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            const now = new Date();
            const day = String(now.getDate()).padStart(2, '0');
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const year = String(now.getFullYear()).slice(-2);
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');

            const filename = `CyberLog ${aziendaDettagli.nome_azienda} (Data ${day}-${month}-${year})(Orario ${hours}-${minutes}).pdf`;
            a.download = filename;

            a.click();
            window.URL.revokeObjectURL(url); // pulizia
        } catch (err) {
            const errorMessage = err.message || t('errors.downloadPdfFailed');
            toast.error(errorMessage);
        }
        finally {
            setLoading(false)
        }
    };

    return (
        <div className="w-full h-fit mx-auto">
            {isVisibleDescriptionBanner && (
                <DescriptionBanner
                    title={t('nis2.title')}
                    description={t('nis2.description')}
                />
            )}

            <div className="flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-x-10">
                <div className="h-64 sm:h-80 w-full sm:w-1/2">
                    <SpiderChart data={data} />
                </div>

                <div className="flex flex-col justify-center gap-6 sm:gap-10 w-full sm:w-1/3">
                    <div className="flex gap-2 items-center font-semibold">
                        <p className="text-4xl">
                            <CountUpAnimation end={percentuale} />%
                        </p>
                        <p>{t('nis2.nis2_risk_score')}</p>
                    </div>

                    <div className="flex gap-2 items-center font-semibold whitespace-nowrap">
                        <CriticalityBadge punteggio={percentuale} />
                        <p>{t('nis2.criticality_level')}</p>
                    </div>

                    {isVisibleDownloadReport && (
                        <button
                            className="flex w-44 gap-2 justify-center items-center bg-yellow-400 px-5 py-2 text-black font-semibold rounded 
                                       duration-300 hover:bg-yellow-500 transition-colors active:scale-95 
                                       disabled:opacity-50 disabled:cursor-not-allowed 
                                     disabled:hover:bg-yellow-400 disabled:active:scale-100 disabled:transition-none"
                            onClick={onExportPdf}
                            disabled={loading || disableForEnterprise}
                        >
                            {loading ? (
                                <div className="flex justify-center w-full">
                                    <ClipLoader size={24} />
                                </div>
                            ) : (
                                <>
                                    <Icon name="listcheck" /> {t('nis2.download_report')}
                                </>
                            )}
                        </button>

                    )}
                </div>
            </div>
        </div>
    );
}
